import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ArrowRight, ShoppingBag } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { imgUrl, formatINR } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";

export default function CartPage() {
  const { cart, updateQty, removeFromCart, cartTotal } = useStore();

  if (cart.length === 0) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-28 text-center" data-testid="cart-empty">
        <Seo title="Your Bag — Anaya Fashion & Gifts" path="/cart" noindex />
        <ShoppingBag className="mx-auto text-primary/40" size={48} />
        <h1 className="mt-4 font-display text-4xl">Your bag is empty</h1>
        <p className="mt-3 text-sm font-light text-muted-foreground">Fill it with something lovely.</p>
        <Link to="/shop" data-testid="cart-shop-link" className="mt-6 inline-block rounded-full bg-foreground px-7 py-3 text-xs tracking-[0.2em] text-white uppercase hover:bg-primary">Start shopping</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6" data-testid="cart-page">
      <Seo title="Your Bag — Anaya Fashion & Gifts" path="/cart" noindex />
      <Reveal>
        <p className="text-[11px] tracking-[0.4em] text-primary uppercase">Almost yours</p>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl">Your bag</h1>
      </Reveal>
      <div className="mt-10 grid gap-10 lg:grid-cols-[1fr_320px]">
        <ul className="space-y-4" data-testid="cart-items">
          {cart.map((item) => {
            const itemKey = item.key || item.id;
            return (
              <Reveal key={itemKey}>
                <li className="glass flex gap-4 rounded-2xl p-4" data-testid={`cart-item-${itemKey}`}>
                  <Link to={`/product/${item.id}`} className="h-24 w-20 shrink-0 overflow-hidden rounded-xl bg-secondary/50">
                    {item.image && <img src={imgUrl(item.image)} alt={item.name} className="h-full w-full object-cover" />}
                  </Link>
                  <div className="flex flex-1 flex-col">
                    <Link to={`/product/${item.id}`}><h3 className="font-display text-base leading-snug">{item.name}</h3></Link>
                    {item.variant && (
                      <span className="mt-1 w-fit rounded-full bg-accent px-2.5 py-0.5 text-[10px] font-medium tracking-widest text-accent-foreground uppercase" data-testid={`cart-variant-${itemKey}`}>
                        Design: {item.variant}
                      </span>
                    )}
                    <p className="mt-1 text-sm text-primary">{formatINR(item.price)}</p>
                    <div className="mt-auto flex items-center justify-between pt-2">
                      <div className="flex items-center rounded-full border border-input bg-white/70">
                        <button data-testid={`cart-qty-minus-${itemKey}`} onClick={() => updateQty(itemKey, item.qty - 1)} className="p-2" aria-label="Decrease"><Minus size={13} /></button>
                        <span className="w-7 text-center text-sm">{item.qty}</span>
                        <button data-testid={`cart-qty-plus-${itemKey}`} onClick={() => updateQty(itemKey, item.qty + 1)} className="p-2" aria-label="Increase"><Plus size={13} /></button>
                      </div>
                      <button data-testid={`cart-remove-${itemKey}`} onClick={() => removeFromCart(itemKey)} aria-label="Remove" className="rounded-full p-2 text-muted-foreground hover:text-destructive">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                  <p className="hidden self-center font-medium sm:block">{formatINR(item.price * item.qty)}</p>
                </li>
              </Reveal>
            );
          })}
        </ul>
        <Reveal delay={0.1}>
          <div className="glass-deep sticky top-28 rounded-3xl p-6" data-testid="cart-summary">
            <p className="font-display text-xl">Summary</p>
            <div className="mt-4 space-y-2 text-sm font-light">
              <div className="flex justify-between"><span>Subtotal</span><span data-testid="cart-subtotal">{formatINR(cartTotal)}</span></div>
              <div className="flex justify-between"><span>Shipping</span><span data-testid="cart-shipping">₹70 prepaid · ₹120 COD</span></div>
            </div>
            <div className="mt-4 flex justify-between border-t border-input pt-4 font-medium">
              <span>Total before shipping</span><span data-testid="cart-total">{formatINR(cartTotal)}</span>
            </div>
            <p className="mt-2 text-[11px] font-light text-muted-foreground">Shipping is added at checkout: ₹70 for prepaid (UPI/bank) or ₹120 for Cash on Delivery.</p>
            <Link to="/checkout" data-testid="checkout-cta"
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-foreground py-3.5 text-xs font-medium tracking-[0.2em] text-white uppercase transition-colors hover:bg-primary">
              Checkout <ArrowRight size={14} />
            </Link>
            <p className="mt-3 text-center text-[11px] font-light text-muted-foreground">COD, UPI &amp; bank transfer accepted</p>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
